'use client';

import { useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';

/**
 * Result-ordering control, shown only while a location filter is active (the
 * server page gates the render): "Newest first" (default rank/recency) vs
 * "Nearest first" (`sort=near` → distance-keyset ordering in the search RPC).
 * URL-driven like the facets; changing the sort resets the keyset cursor.
 */
export function ListingSort() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useTranslation();

  const value = searchParams.get('sort') === 'near' ? 'near' : 'newest';

  const applySort = useCallback(
    (next: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('cursor');
      if (next === 'near') {
        params.set('sort', 'near');
      } else {
        params.delete('sort');
      }
      const qs = params.toString();
      router.push(qs ? `/marketplace?${qs}` : '/marketplace');
    },
    [router, searchParams],
  );

  return (
    <Select value={value} onValueChange={applySort}>
      <SelectTrigger
        aria-label={t('explore:sortControl.ariaLabel')}
        className="h-9 w-auto gap-1.5 text-sm"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        <SelectItem value="newest">
          {t('explore:sortControl.newestFirst')}
        </SelectItem>
        <SelectItem value="near">
          {t('explore:sortControl.nearestFirst')}
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
